import { computed, ref, type MaybeRefOrGetter } from 'vue'
import { StripeMinimumChargeAmount, StripeZeroDecimalCurrencies } from '../constants/payment.constants'

import { currency } from '../utils/currency'
import { usePaymentService } from './usePaymentService'
import { usePaymentUIStore } from '../stores/payment.ui-store'
import { useTaxQueryStore } from '../stores/tax.query-store'
import type { PAYMENT_ENTITY_TYPE, PaymentCallbackEvent, PaymentModalProps } from '../types/payment.types'

/**
 * Main Payment Composable
 * Orchestrates payment functionality
 */
export const usePayment = () => {
  const paymentService = usePaymentService()
  const paymentUIStore = usePaymentUIStore()
  const taxQueryStore = useTaxQueryStore()

  // Local state
  const error = ref<string | null>(null)

  /**
   * Open payment modal with configuration
   */
  const openPaymentModal = (props: PaymentModalProps) => {
    paymentUIStore.openPaymentModal(props)
    error.value = null
  }

  /**
   * Close payment modal
   */
  const closePaymentModal = () => {
    paymentUIStore.closePaymentModal()
    error.value = null
  }

  /**
   * Format currency amount for display
   */
  const formatCurrency = (amount: number, currencyCode: string): string => {
    const currencyInfo = currency[currencyCode]
    if (!currencyInfo) return `${amount}`

    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: currencyInfo.decimal_digits,
      maximumFractionDigits: currencyInfo.decimal_digits,
    })

    return formatter.format(amount)
  }

  /**
   * Get currency information
   */
  const getCurrencyInfo = (currencyCode: string) => {
    return currency[currencyCode] || null
  }

  /**
   * Get minimum charge amount for a currency
   */
  const getMinimumChargeAmount = (currencyCode: string): number => {
    return StripeMinimumChargeAmount[currencyCode as keyof typeof StripeMinimumChargeAmount] || 1
  }

  /**
   * Check if currency is zero decimal
   */
  const isZeroDecimalCurrency = (currencyCode: string): boolean => {
    return StripeZeroDecimalCurrencies.includes(currencyCode)
  }

  /**
   * Convert amount to cents for Stripe
   */
  const convertToCents = (amount: number, currencyCode: string): number => {
    return isZeroDecimalCurrency(currencyCode) ? Math.round(amount) : Math.round(amount * 100)
  }

  /**
   * Convert cents to amount
   */
  const convertFromCents = (amount: number, currencyCode: string): number => {
    return isZeroDecimalCurrency(currencyCode) ? amount : amount / 100
  }

  /**
   * Process payment for a group
   */
  const processGroupPayment = async (params: {
    groupId: string
    locationId: string
    contactDetails: {
      id: string
      fullName: string
      email: string
      phone?: string
    }
    membership: {
      price: number
      currency: string
      paymentPriceId: string
      paymentProductId: string
      subscriptionType: 'ONE_TIME' | 'RECURRING'
      subscriptionInterval?: 'WEEK' | 'MONTH' | 'YEAR'
      trialDays?: number
      isLivePayment: boolean
    }
    sourceId: string
    sourceType: PAYMENT_ENTITY_TYPE
    productName: string
    logo?: string
    modalTitle: string
    onOrderCreated?: (data: { orderId: string; sourceId: string }) => Promise<void>
  }) => {
    const { membership, ...rest } = params

    const modalProps = {
      show: true,
      priceId: membership.paymentPriceId,
      productId: membership.paymentProductId,
      productName: rest.productName,
      amount: membership.price,
      currency: membership.currency,
      subscriptionType: membership.subscriptionType,
      subscriptionInterval: membership.subscriptionInterval,
      trialDays: membership.trialDays,
      isLivePayment: membership.isLivePayment,
      locationId: rest.locationId,
      contactDetails: rest.contactDetails,
      sourceId: rest.sourceId,
      sourceType: rest.sourceType,
      groupId: rest.groupId,
      logo: rest.logo,
      modalTitle: rest.modalTitle,
      onOrderCreated: rest.onOrderCreated,
    }
    openPaymentModal(modalProps)
  }

  /**
   * Process payment for a course
   */
  const processCoursesPayment = async (params: {
    locationId: string
    contactDetails: {
      id: string
      fullName: string
      email: string
      phone?: string
    }
    course: {
      price: number
      currency: string
      paymentPriceId: string
      paymentProductId: string
      subscriptionType: 'ONE_TIME' | 'RECURRING'
      subscriptionInterval?: 'WEEK' | 'MONTH' | 'YEAR'
      trialDays?: number
      isLivePayment: boolean
    }
    sourceId: string
    sourceType: PAYMENT_ENTITY_TYPE
    groupId?: string
    entityType?: string
    productName: string
    logo?: string
    modalTitle: string
  }) => {
    const { course, ...rest } = params

    const modalProps = {
      show: true,
      priceId: course.paymentPriceId,
      productId: course.paymentProductId,
      productName: rest.productName,
      amount: course.price,
      currency: course.currency,
      subscriptionType: course.subscriptionType,
      subscriptionInterval: course.subscriptionInterval,
      trialDays: course.trialDays,
      isLivePayment: course.isLivePayment,
      locationId: rest.locationId,
      contactDetails: rest.contactDetails,
      sourceId: rest.sourceId,
      sourceType: rest.sourceType,
      groupId: rest.groupId,
      entityType: rest.entityType,
      logo: rest.logo,
      modalTitle: rest.modalTitle,
    }
    openPaymentModal(modalProps)
  }

  /**
   * Handle payment success
   */
  const handlePaymentSuccess = async (data: PaymentCallbackEvent) => {
    console.log('Payment successful:', data)
    closePaymentModal()
    // You can add additional success handling here
  }

  /**
   * Handle payment error
   */
  const handlePaymentError = (errorData: PaymentCallbackEvent) => {
    console.error('Payment failed:', errorData)
    error.value = errorData.message || 'Payment failed'
  }

  /**
   * Set error message
   */
  const setError = (errorMessage: string | null) => {
    error.value = errorMessage
  }

  /**
   * Clear error message
   */
  const clearError = () => {
    error.value = null
  }

  /**
   * Factory for the location-level automatic-tax flag.
   *
   * Single source of truth for `automaticTaxesEnabled`: backed by a TanStack
   * query keyed on `locationId` with `staleTime: Infinity`, so multiple
   * subscribers (e.g. `useGroupInitialization` eager-loading at group resolve
   * + `PaymentModal` reading at open time) share one cache entry per location.
   *
   * Consumers must read `isTaxConfigSettled` before branching on
   * `automaticTaxesEnabled` — while the query is in-flight the flag reads
   * `false` because `query.data` is undefined, which is indistinguishable
   * from a loaded "tax disabled" response. The settled flag distinguishes
   * "loading" from "loaded-and-false" so callers can defer step decisions
   * (or await `query.suspense()` directly) instead of locking the modal
   * into the legacy single-step flow on a cold cache.
   *
   * Failure mode: `retry: false` and `data` stays undefined on error → the
   * `automaticTaxesEnabled` computed reads as `false`, matching the original
   * silent-fail contract (tax UI never blocks checkout).
   */
  const fetchTaxAutomaticConfigQuery = (locationId: MaybeRefOrGetter<string | undefined>) => {
    const query = taxQueryStore.fetchTaxAutomaticConfigQuery(locationId)
    const automaticTaxesEnabled = computed(() => Boolean(query.data.value?.automaticTaxesEnabled))
    const isTaxConfigSettled = computed(() => !query.isLoading.value)
    return { query, automaticTaxesEnabled, isTaxConfigSettled }
  }

  return {
    // State from UI store
    isPaymentModalOpen: computed(() => paymentUIStore.isModalVisible),
    paymentModalProps: computed(() => paymentUIStore.paymentModalProps),
    error: computed(() => error.value),
    store: paymentUIStore,

    // Modal methods
    openPaymentModal,
    closePaymentModal,

    // Payment processing
    processGroupPayment,
    processCoursesPayment,
    handlePaymentSuccess,
    handlePaymentError,

    // Currency utilities
    formatCurrency,
    getCurrencyInfo,
    getMinimumChargeAmount,
    isZeroDecimalCurrency,
    convertToCents,
    convertFromCents,

    // State management
    setError,
    clearError,

    // Tax query store (server state)
    fetchCountryStatesQuery: taxQueryStore.fetchCountryStatesQuery,
    amountSummaryMutation: taxQueryStore.amountSummaryMutation,
    fetchTaxAutomaticConfigQuery,

    // Service methods
    ...paymentService,
  }
}
